import { useMemo } from 'react';

import useAppContext from 'flow/hooks/useAppContext';
import { useAppSelector } from 'flow/hooks/useRedux';
import {
    selectHasChatSessions,
    selectSortedAndFilteredChatSessionKeys,
} from 'flow/selectors/chat-selectors';
import { getSearchText } from 'flow/selectors/selectors';
import { ChatListRow } from './chat-list-rows';

type ChatSessionRows = {
    rows: ChatListRow[];
    /** Stands in for the chat rows while there are none, and belongs last. */
    emptyStateRow?: ChatListRow;
};

/** The two fixed entries at the top and a row per chat session, all narrowed
 *  by the search text - searching narrows this list rather than replacing it.
 *  A fixed entry is matched on its label as shown, the same way a chat is
 *  matched on its name. Hiding the row of the open page only hides the row:
 *  the page stays open. */
const useChatSessionRows = (): ChatSessionRows => {
    const { i18next } = useAppContext();
    const searchText = useAppSelector(getSearchText);
    const activeChatSessionKeys = useAppSelector(
        selectSortedAndFilteredChatSessionKeys,
    );
    const hasChatSessions = useAppSelector(selectHasChatSessions);

    // The labels the fixed entries show, so a change of language counts.
    const overviewLabel = i18next.t('messages.overview');
    const meetingsLabel = i18next.t('meetings.title');
    const fixedRows = useMemo(() => {
        const lowerCaseSearch = searchText ? searchText.toLowerCase() : '';
        const matches = (label: string) =>
            label.toLowerCase().includes(lowerCaseSearch);
        const rows: ChatListRow[] = [];
        if (matches(overviewLabel)) {
            rows.push({ kind: 'overview' });
        }
        if (matches(meetingsLabel)) {
            rows.push({ kind: 'meetings' });
        }
        return rows;
    }, [overviewLabel, meetingsLabel, searchText]);

    return useMemo<ChatSessionRows>(
        () => ({
            rows: [
                ...fixedRows,
                ...activeChatSessionKeys
                    .map(
                        (chatSessionKey): ChatListRow => ({
                            kind: 'chatSession',
                            chatSessionKey,
                        }),
                    )
                    .toArray(),
            ],
            emptyStateRow: hasChatSessions
                ? undefined
                : { kind: 'noChatSessions' },
        }),
        [fixedRows, activeChatSessionKeys, hasChatSessions],
    );
};

export default useChatSessionRows;
